import { defineStore, storeToRefs } from "pinia";
import { ref, computed, watch } from "vue";
import { useSurveyStore } from "@stores/survey/survey";
import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
import { useCurrentUserStore } from "@stores/currentUser";
import useMyFetch from "@stores/useMyFetch";
import { fetchChartsFilteredData, clearChartsCache } from "@utils/charts";
import { useToastsStore } from "@/stores/toastsStore";
import genToken from "@utils/generateToken";
import router from "@/routes";
import { useResultsV2CardsStore } from "./resultsV2.cards";
import { useResultsV2PopulationsStore } from "./resultsV2.populations";
import { useResultsV2RecodesStore } from "./resultsV2.recodes";
import { captureEvent } from "@/lib/analytics";

export const useResultsV2Store = defineStore("resultsV2", () => {
  const toast = useToastsStore();
  const surveyStore = useSurveyStore();
  const { token: surveyToken, survey, resultsApiPath, sharedDashboardToken } = storeToRefs(surveyStore);
  const populationsStore = useResultsV2PopulationsStore();
  const { populations } = storeToRefs(populationsStore);
  const { resetForView, fetchPopulations } = populationsStore;

  function mergePopulationData(target, sourceData, popKey) {
    for (const [key, value] of Object.entries(sourceData)) {
      if (!target[key]) target[key] = {};
      target[key] = { ...target[key], [popKey]: value };
    }
  }

  // Core state — view metadata only; cards/populations live in their own stores
  const views = ref([]);
  const currentView = ref(null);
  const isDefaultView = ref(false);
  const editMode = ref(true);
  const loading = ref(false);
  const saving = ref(false);
  const viewDirty = ref(false);
  const printing = ref(false);
  const printTallCardIds = ref(new Set());

  const availableCards = ref([]);
  const allQuestionCards = ref([]);
  const chartsData = ref({});
  const significance = ref({});
  const showAllPopulation = ref(true);
  const allTemporarilyHidden = ref(false);
  const effectiveShowAllPopulation = computed(
    () => showAllPopulation.value && !allTemporarilyHidden.value,
  );
  const importancePerformanceData = ref({});

  const viewName = computed(() => {
    if (isDefaultView.value) return "Default dashboard";
    return currentView.value?.name || "Dashboard";
  });

  const baseRespondents = computed(() =>
    Object.values(chartsData.value || {}).reduce((max, q) => Math.max(max, q?.respondents || 0), 0),
  );

  // Layout (list | grid): ?layout in the URL is the viewer-side override, and its
  // absence means "use the view's saved default". The URL is the only source, so
  // nothing here has to be reset — dropping the param restores the default.
  const viewLayout = computed(
    () =>
      router.currentRoute.value.query.layout ||
      currentView.value?.options?.layout ||
      "list",
  );

  // Tracks recode cards whose codification job is running. Survives SPA navigation
  // so the loader stays visible even if the user leaves the page and comes back.
  const regeneratingCardIds = ref(new Set());

  function markCardRegenerating(cardId) {
    regeneratingCardIds.value = new Set(regeneratingCardIds.value).add(cardId);
  }

  function clearCardRegenerating(cardId) {
    if (!regeneratingCardIds.value.has(cardId)) return;
    const next = new Set(regeneratingCardIds.value);
    next.delete(cardId);
    regeneratingCardIds.value = next;
  }

  const savedPopulations = ref([]);

  function viewsPath() {
    return `/surveys/${surveyToken.value}/results_views`;
  }

  function viewPath(viewId) {
    return `${viewsPath()}/${viewId}`;
  }

  const hasUnsavedChanges = computed(
    () => useResultsV2CardsStore().hasUnsavedChanges || viewDirty.value,
  );

  const displayLanguage = computed(() => {
    const localesStore = useSurveyLocalesStore();
    const token = currentView.value?.options?.locale_token;
    const override = token && localesStore.localeOptions.find((o) => o.value === token);
    return (override || localesStore.analysisLocale)?.language || null;
  });

  function markViewDirty() {
    viewDirty.value = true;
  }

  function setShowAllPopulation(value) {
    showAllPopulation.value = value;
    markViewDirty();
  }

  async function fetchSavedPopulations() {
    const { data } = await useMyFetch(`/surveys/${surveyToken.value}/search_groups`).json();
    savedPopulations.value = data.value || [];
  }

  async function createSavedPopulation({ name, filters, color }) {
    const { data } = await useMyFetch(`/surveys/${surveyToken.value}/search_groups`)
      .post({ name, filters, color })
      .json();
    if (data.value) {
      savedPopulations.value.push(data.value);
      captureEvent("results_population_created");
    }
    return data.value;
  }

  async function updateSavedPopulation(token, { name, filters, color }) {
    const { data } = await useMyFetch(`/surveys/${surveyToken.value}/search_groups/${token}`)
      .put({ name, filters, color })
      .json();
    if (data.value) {
      const idx = savedPopulations.value.findIndex((p) => p.token === token);
      if (idx !== -1) savedPopulations.value[idx] = data.value;
      captureEvent("results_population_updated");
    }
    return data.value;
  }

  async function deleteSavedPopulation(token) {
    const { response } = await useMyFetch(`/surveys/${surveyToken.value}/search_groups/${token}`).delete();
    savedPopulations.value = savedPopulations.value.filter((p) => p.token !== token);

    if (response.value.ok) captureEvent("results_population_deleted");
  }

  async function fetchViews() {
    loading.value = true;
    try {
      const { response, data } = await useMyFetch(viewsPath()).get().json();
      if (response.value.ok) views.value = data.value;
    } finally {
      loading.value = false;
    }
  }

  function setActiveView(viewData, isDefault = false) {
    currentView.value = viewData;
    isDefaultView.value = isDefault;
    showAllPopulation.value = viewData?.options?.show_all_population ?? true;
    allTemporarilyHidden.value = false;
    useResultsV2CardsStore().resetForView();
    resetForView();
  }

  async function loadView(viewId) {
    loading.value = true;
    try {
      const { response, data } = await useMyFetch(viewPath(viewId)).get().json();
      if (!response.value.ok) return;
      setActiveView(data.value, false);
      const viewToken = data.value?.token;
      await Promise.all([
        useResultsV2CardsStore().fetchCards(viewToken),
        fetchPopulations(viewToken),
      ]);
      await fetchAllChartsData(populations.value);
    } finally {
      loading.value = false;
    }
  }

  async function loadDefaultView() {
    loading.value = true;
    try {
      const { response, data } = await useMyFetch(`${viewsPath()}/default`).get().json();
      if (!response.value.ok) return;

      const viewData = data.value || {};
      const embeddedCards = (viewData.cards || []).map((card, i) => ({
        ...card,
        id: card.id ?? `default_${i}`,
      }));
      delete viewData.cards;

      setActiveView(viewData, true);
      useResultsV2CardsStore().setCards(embeddedCards);
      await fetchAllChartsData(populations.value);
    } finally {
      loading.value = false;
    }
  }

  async function createView(params) {
    const { response, data } = await useMyFetch(viewsPath())
      .post({ results_view: params })
      .json();
    if (response.value.ok) {
      views.value.unshift(data.value);
      captureEvent("results_view_created", { view_token: data.value?.token });
      return data.value;
    }
    throw new Error("Failed to create view");
  }

  async function updateView(params, viewToken = null) {
    const token = viewToken || currentView.value?.token;
    if (!token) return;
    const { response, data } = await useMyFetch(viewPath(token))
      .patch({ results_view: params })
      .json();
    if (response.value.ok) {
      if (currentView.value?.token === token) {
        currentView.value = { ...currentView.value, ...data.value };
      }
      const index = views.value.findIndex((v) => v.id === data.value.id);
      if (index !== -1) views.value[index] = data.value;
      return data.value;
    }
    throw new Error("Failed to update view");
  }

  async function deleteView(viewToken) {
    const { response } = await useMyFetch(viewPath(viewToken)).delete();
    if (response.value.ok) {
      views.value = views.value.filter((v) => v.token !== viewToken);
      if (currentView.value?.token === viewToken) clearCurrentView();
      captureEvent("results_view_deleted", { view_token: viewToken });
    }
  }

  function applyShare(viewToken, data) {
    const index = views.value.findIndex((v) => v.token === viewToken);
    if (index !== -1) views.value[index] = data;
    if (currentView.value?.token === viewToken) currentView.value.shared = data.shared;
  }

  async function shareView(viewToken) {
    const { response, data } = await useMyFetch(`${viewPath(viewToken)}/share`).post().json();
    if (response.value.ok) {
      applyShare(viewToken, data.value);
      captureEvent("results_view_shared", { view_token: viewToken });
    }
    return data.value;
  }

  async function unshareView(viewToken) {
    const { response, data } = await useMyFetch(`${viewPath(viewToken)}/unshare`).delete().json();
    if (response.value.ok) {
      applyShare(viewToken, data.value);
      captureEvent("results_view_share_revoked", { view_token: viewToken });
    }
    return data.value;
  }

  async function duplicateView(viewToken) {
    const { response, data } = await useMyFetch(`${viewPath(viewToken)}/duplicate`).post().json();
    if (response.value.ok) {
      views.value.unshift(data.value);
      captureEvent("results_view_duplicated", { view_token: viewToken });
      return data.value;
    }
    throw new Error("Failed to duplicate view");
  }

  async function saveView() {
    if (!currentView.value) return;
    saving.value = true;
    const cardsStore = useResultsV2CardsStore();
    try {
      const localName = currentView.value.name;
      const localGlobalOptions = {
        ...(currentView.value.options || {}),
        show_all_population: showAllPopulation.value,
      };
      const selectedPos = cardsStore.selectedCard?.position;

      await cardsStore.saveAllCards();

      const { response, data } = await useMyFetch(viewPath(currentView.value.token))
        .patch({
          results_view: { name: localName, options: localGlobalOptions },
        })
        .json();
      if (response.value.ok && data.value) {
        currentView.value = { ...currentView.value, ...data.value };
      }

      // Refresh cards from DB (server-assigned IDs) and restore selection
      await cardsStore.fetchCards(currentView.value.token);
      if (selectedPos != null) {
        const restored = cardsStore.cards.find((c) => c.position === selectedPos) || null;
        cardsStore.selectedCard = restored;
      }
      viewDirty.value = false;
    } catch (err) {
      toast.error("Failed to save view", err?.message);
    } finally {
      saving.value = false;
    }
  }

  async function createEmptyView(name) {
    const newView = await createView({ name, options: {} });
    setActiveView(newView, false);
    viewDirty.value = false;
    return newView;
  }

  async function saveAsNew(name) {
    saving.value = true;
    try {
      const cardsStore = useResultsV2CardsStore();
      const sourceCards = cardsStore.cards;
      const sourcePopulations = populations.value;

      const newView = await createView({
        name,
        options: {
          ...(currentView.value?.options || {}),
          show_all_population: showAllPopulation.value,
        },
      });

      const cardsBasePath = `/surveys/${surveyToken.value}/results_views/${newView.token}/cards`;
      const cardCreations = sourceCards
        .filter((card) => !(card.card_type === "recode" && !card.card_id))
        .map((card) =>
          useMyFetch(cardsBasePath)
            .post({
              card: {
                card_type: card.card_type,
                card_id: card.card_id,
                position: card.position,
                content: card.content || {},
                options: card.options,
              },
            })
            .json(),
        );

      const popsBasePath = `/surveys/${surveyToken.value}/results_views/${newView.token}/results_view_populations`;
      const populationCreations = sourcePopulations.map((pop) =>
        useMyFetch(popsBasePath)
          .post({ results_view_population: { search_group_id: pop.search_group?.id } })
          .json(),
      );

      await Promise.all([...cardCreations, ...populationCreations]);

      return newView;
    } finally {
      saving.value = false;
    }
  }

  function buildCustomBases() {
    const customBases = {};
    const viewCards = useResultsV2CardsStore().cards;
    for (const card of viewCards) {
      const meta = availableCards.value.find((c) => c.id === card.card_id);
      if (!meta) continue;

      const customBase = card.options?.custom_base;
      if (customBase) {
        (meta.questions || []).forEach((q) => {
          customBases[q.id] = customBase;
        });
      }
    }
    return customBases;
  }

  async function fetchAvailableCards() {
    const { response, data } = await useMyFetch(
      `/surveys/${surveyToken.value}/cards?type=questions&is_charts=true&with_questions=true${useCurrentUserStore().isAdmin ? "" : "&is_admin=false"}`,
    )
      .get()
      .json();
    if (response.value.ok) availableCards.value = data.value || [];
  }

  let allQuestionCardsPromise = null;
  function fetchAllQuestionCards() {
    allQuestionCardsPromise ||= (async () => {
      const { execute, response, data } = useMyFetch(
        `/surveys/${surveyToken.value}/cards?type=questions&with_questions=true${useCurrentUserStore().isAdmin ? "" : "&is_admin=false"}`,
        { immediate: false },
      )
        .get()
        .json();
      await execute();
      if (response.value?.ok) allQuestionCards.value = data.value || [];
      else allQuestionCardsPromise = null;
    })();
    return allQuestionCardsPromise;
  }

  // Card-level custom_populations not in the view still need their data fetched,
  // else the card renders without them after a reload.
  function collectCardCustomPopulations(viewPopulations) {
    const seen = new Set(
      (viewPopulations || []).map((p) => p.search_group?.token).filter(Boolean),
    );
    const extra = [];
    for (const card of useResultsV2CardsStore().cards) {
      // Chart cards keep their extra populations in custom_populations; KPI trend
      // analyses keep the waves to plot in options.populations. Both need fetching.
      const tokenLists = [card.options?.custom_populations];
      if (card.content?.sub_type === "kpi_trend") tokenLists.push(card.options?.populations);
      for (const tokens of tokenLists) {
        if (!Array.isArray(tokens)) continue;
        for (const token of tokens) {
          if (!token || token === "all" || seen.has(token)) continue;
          const sg = savedPopulations.value.find((s) => s.token === token);
          if (!sg) continue;
          seen.add(token);
          extra.push({ id: `card_${token}`, search_group: sg });
        }
      }
    }
    return extra;
  }

  async function fetchAllChartsData(viewPopulations = []) {
    // Card populations resolve against savedPopulations, so ensure they're loaded.
    if (!savedPopulations.value.length && !sharedDashboardToken.value) await fetchSavedPopulations();
    const populations = [...viewPopulations, ...collectCardCustomPopulations(viewPopulations)];

    const customBases = buildCustomBases();

    const { data, status } = await fetchChartsFilteredData({
      surveyToken: surveyToken.value,
      filters: {},
      target: 0,
      surveyUpdatedAt: survey.value?.updated_at,
      customBases,
      includeNumeric: true,
    });

    const merged = status !== "CANCELED" && data?.value ? { ...data.value } : { ...chartsData.value };
    const allData = status !== "CANCELED" && data?.value ? data.value : {};

    const noFilterPops = populations.filter(
      (pop) => !pop.search_group?.filters || Object.keys(pop.search_group.filters).length === 0,
    );
    const filterPops = populations.filter(
      (pop) => pop.search_group?.filters && Object.keys(pop.search_group.filters).length > 0,
    );

    for (const pop of noFilterPops) {
      mergePopulationData(merged, allData, `pop_sg_${pop.search_group?.token}`);
    }

    const results = await Promise.all(
      filterPops.map((pop) => {
        const popKey = `pop_sg_${pop.search_group?.token}`;
        return fetchChartsFilteredData({
          surveyToken: surveyToken.value,
          filters: pop.search_group.filters,
          target: 0,
          surveyUpdatedAt: `${survey.value?.updated_at}_${popKey}`,
          customBases,
          includeNumeric: true,
        }).then((result) => ({ ...result, popKey }));
      }),
    );

    for (const { data: popData, status: popStatus, popKey } of results) {
      if (popStatus !== "CANCELED" && popData?.value) {
        mergePopulationData(merged, popData.value, popKey);
      }
    }

    chartsData.value = merged;

    await fetchSignificance(populations);
  }

  function extractQuestionsForGroup(popKey = null) {
    const result = {};
    for (const [qId, qData] of Object.entries(chartsData.value)) {
      const data = popKey ? qData?.[popKey] : qData;
      if (!data?.answers) continue;
      result[qId] = {
        total: data.respondents || 0,
        answers: Object.fromEntries(
          Object.entries(data.answers).map(([code, ans]) => [code, ans.nb || 0]),
        ),
      };
    }
    return result;
  }

  function extractSignificanceForGroup(codePairwise, groupId, groupIdToName) {
    const superiorTo = [];
    const inferiorTo = [];
    Object.values(codePairwise || {}).forEach((comparison) => {
      if (!comparison || !comparison.significance) return;
      const isGroup1 = comparison.group_1 === groupId;
      const isGroup2 = comparison.group_2 === groupId;
      if (!isGroup1 && !isGroup2) return;
      const otherGroupId = isGroup1 ? comparison.group_2 : comparison.group_1;
      const otherGroupName = groupIdToName[otherGroupId] || otherGroupId;
      const isSuper = comparison.significance === "+" ? isGroup1 : isGroup2;
      (isSuper ? superiorTo : inferiorTo).push(otherGroupName);
    });
    let direction = null;
    if (superiorTo.length > 0 && inferiorTo.length === 0) direction = "+";
    else if (inferiorTo.length > 0 && superiorTo.length === 0) direction = "-";
    else if (superiorTo.length > 0 && inferiorTo.length > 0) direction = "mixed";
    return { direction, superiorTo, inferiorTo };
  }

  async function fetchSignificance(populations) {
    const groups = [];
    if (effectiveShowAllPopulation.value) {
      groups.push({ id: "all", popKey: "all", name: "All", questions: extractQuestionsForGroup() });
    }
    (populations || []).forEach((pop) => {
      const popKey = `pop_sg_${pop.search_group?.token}`;
      groups.push({
        id: pop.search_group?.token || `pop_${pop.id}`,
        popKey,
        name: pop.search_group?.name || "Population",
        questions: extractQuestionsForGroup(popKey),
      });
    });

    if (groups.length < 2) {
      significance.value = {};
      return;
    }

    try {
      const { data } = await useMyFetch(`${resultsApiPath.value}/charts/significance`)
        .post({ groups: groups.map(({ id, questions }) => ({ id, questions })) })
        .json();
      const pairwise = data.value?.pairwise_significance;
      if (!pairwise) {
        significance.value = {};
        return;
      }

      const groupIdToName = Object.fromEntries(groups.map((g) => [g.id, g.name]));
      const lookup = {};
      groups.forEach(({ id, popKey }) => {
        Object.entries(pairwise).forEach(([questionId, qPairwise]) => {
          Object.entries(qPairwise || {}).forEach(([code, codePairwise]) => {
            const result = extractSignificanceForGroup(codePairwise, id, groupIdToName);
            if (!result.direction && !result.superiorTo.length && !result.inferiorTo.length) return;
            if (!lookup[questionId]) lookup[questionId] = {};
            if (!lookup[questionId][popKey]) lookup[questionId][popKey] = {};
            lookup[questionId][popKey][code] = result;
          });
        });
      });
      significance.value = lookup;
    } catch {
      significance.value = {};
    }
  }

  watch(effectiveShowAllPopulation, () => {
    fetchSignificance(populations.value);
  });

  async function fetchPopulationChartData(pop) {
    const popKey = `pop_sg_${pop.token}`;

    const anyQuestion = Object.keys(chartsData.value)[0];
    if (anyQuestion && chartsData.value[anyQuestion]?.[popKey]) return;

    const hasFilters = pop.filters && Object.keys(pop.filters).length > 0;
    const merged = { ...chartsData.value };

    if (!hasFilters) {
      for (const [key, value] of Object.entries(merged)) {
        if (typeof value === "object" && value !== null && value.answers) {
          merged[key] = { ...value, [popKey]: value };
        }
      }
    } else {
      const customBases = buildCustomBases();
      const { data: popData, status: popStatus } = await fetchChartsFilteredData({
        surveyToken: surveyToken.value,
        filters: pop.filters,
        target: 0,
        surveyUpdatedAt: `${survey.value?.updated_at}_${popKey}`,
        customBases,
        includeNumeric: true,
      });
      if (popStatus !== "CANCELED" && popData?.value) {
        mergePopulationData(merged, popData.value, popKey);
      }
    }

    chartsData.value = merged;

    await fetchSignificance(populations.value);
  }

  async function refreshChartsData({ bypassCache = false } = {}) {
    if (bypassCache) clearChartsCache();
    await fetchAllChartsData(populations.value);
  }

  // Importance-Performance data
  async function fetchImportancePerformanceData(cardId, { globalQuestionId, matrixQuestionIds, filters }) {
    const viewPopulations = currentView.value?.population_configs || [];

    const buildBody = (popFilters) => ({
      global_question_id: globalQuestionId,
      matrix_question_ids: matrixQuestionIds,
      filters: popFilters || {},
    });

    const fetchOne = async (popFilters) =>
      useMyFetch(`${resultsApiPath.value}/importance_performance`)
        .post(buildBody(popFilters))
        .json();

    const [allResult, ...popResults] = await Promise.all([
      fetchOne(filters),
      ...viewPopulations.map((pop) => {
        const sg = savedPopulations.value.find((s) => s.id === pop.search_group_id);
        return sg ? fetchOne(sg.filters) : Promise.resolve(null);
      }),
    ]);

    const results = [];
    if (allResult?.response?.value?.ok && allResult?.data?.value) {
      results.push({ key: "all", label: "All", data: allResult.data.value });
    }
    viewPopulations.forEach((pop, idx) => {
      const sg = savedPopulations.value.find((s) => s.id === pop.search_group_id);
      const r = popResults[idx];
      if (sg && r?.response?.value?.ok && r?.data?.value) {
        results.push({ key: `pop_sg_${sg.token}`, label: sg.name, color: sg.color, data: r.data.value });
      }
    });

    importancePerformanceData.value = { ...importancePerformanceData.value, [cardId]: results };
  }

  function setEditMode(mode) {
    editMode.value = mode;
  }

  function recodeTokenForViewCard(viewCard) {
    if (viewCard._recodeToken) return viewCard._recodeToken;
    if (viewCard.card_id == null) return null;
    return availableCards.value.find((c) => c.id === viewCard.card_id)?.token || null;
  }

  function cancelOpenRecodeForms() {
    const recodesStore = useResultsV2RecodesStore();
    const cardsStore = useResultsV2CardsStore();
    const open = cardsStore.cards.filter((c) => cardsStore.isConfiguring(c.id));
    open.forEach((card) => {
      const token = recodeTokenForViewCard(card);
      if (token) recodesStore.cleanup(token);
      cardsStore.closeConfig(card.id);
      if (card._isNew && card._recodeToken) cardsStore.removeCard(card.id);
    });
  }

  function addRecodeCard(position, recodeKind = "conditions") {
    const recodesStore = useResultsV2RecodesStore();
    const cardsStore = useResultsV2CardsStore();
    const recodeToken = genToken();

    recodesStore.initNewRecode(recodeToken, recodeKind);

    const newCard = cardsStore.addCard("recode", null, position);
    if (newCard) {
      newCard._recodeToken = recodeToken;
      newCard._recodeKind = recodeKind;
      cardsStore.openConfig(newCard.id);
    }
    return newCard;
  }

  function duplicateRecodeCard(sourceRecodeToken, position) {
    const recodesStore = useResultsV2RecodesStore();
    const cardsStore = useResultsV2CardsStore();
    const recodeToken = genToken();

    const recodeKind = recodesStore.duplicateRecode(sourceRecodeToken, recodeToken);
    if (!recodeKind) return null;

    const newCard = cardsStore.addCard("recode", null, position);
    if (newCard) {
      newCard._recodeToken = recodeToken;
      newCard._recodeKind = recodeKind;
      cardsStore.openConfig(newCard.id);
    }
    return newCard;
  }

  async function recodeComputeCompleted(questions) {
    const recodesStore = useResultsV2RecodesStore();
    const cardsStore = useResultsV2CardsStore();

    // Recode recomputes rewrite answers without bumping survey.updated_at (the
    // memoize key of the charts fetch), so the cache must be cleared by hand.
    clearChartsCache();
    await surveyStore.fetch(surveyToken.value);
    await fetchAvailableCards();
    await fetchAllChartsData(populations.value);

    (questions || []).forEach((q) => {
      if (!q.card_token) return;
      const viewCard = cardsStore.cards.find((c) => c._recodeToken === q.card_token);
      if (viewCard) {
        const meta = availableCards.value.find((c) => c.token === q.card_token);
        if (meta) viewCard.card_id = meta.id;
        delete viewCard._isNew;
      }
      // Clear the regenerating flag for cards matching this recode (any view)
      const meta = availableCards.value.find((c) => c.token === q.card_token);
      if (meta) clearCardRegenerating(meta.id);
    });

    (questions || []).forEach((q) => {
      if (q.card_token) recodesStore.stopLoading(q.card_token);
    });
  }

  function clearCurrentView() {
    currentView.value = null;
    isDefaultView.value = false;
    viewDirty.value = false;
    useResultsV2CardsStore().resetForView();
    resetForView();
  }

  function resetState() {
    clearCurrentView();
    views.value = [];
    saving.value = false;
    loading.value = false;
    printing.value = false;
    printTallCardIds.value = new Set();
    availableCards.value = [];
    allQuestionCards.value = [];
    allQuestionCardsPromise = null;
    chartsData.value = {};
    significance.value = {};
    savedPopulations.value = [];
    showAllPopulation.value = true;
    allTemporarilyHidden.value = false;
  }

  return {
    views,
    currentView,
    isDefaultView,
    editMode,
    loading,
    saving,
    printing,
    printTallCardIds,
    availableCards,
    allQuestionCards,
    chartsData,
    viewName,
    baseRespondents,
    significance,
    showAllPopulation,
    allTemporarilyHidden,
    effectiveShowAllPopulation,
    importancePerformanceData,
    setShowAllPopulation,
    displayLanguage,
    viewLayout,

    regeneratingCardIds,
    markCardRegenerating,
    clearCardRegenerating,

    hasUnsavedChanges,

    fetchViews,
    loadView,
    setActiveView,
    loadDefaultView,
    createView,
    updateView,
    deleteView,
    duplicateView,
    shareView,
    unshareView,
    saveView,
    saveAsNew,
    createEmptyView,
    clearCurrentView,
    resetState,

    fetchAvailableCards,
    fetchAllQuestionCards,
    fetchAllChartsData,
    fetchPopulationChartData,
    refreshChartsData,
    fetchImportancePerformanceData,

    setEditMode,

    markViewDirty,

    savedPopulations,
    fetchSavedPopulations,
    createSavedPopulation,
    updateSavedPopulation,
    deleteSavedPopulation,

    recodeTokenForViewCard,
    cancelOpenRecodeForms,
    addRecodeCard,
    duplicateRecodeCard,
    recodeComputeCompleted,

  };
});
